import { useQuery } from '@tanstack/react-query';
import { ReactNode } from 'react';
import { toast } from 'sonner';
import { aiApi, isAiGenerationCancelled } from '@/api/ai';
import { ClipboardText, DocumentText } from '@/components/icons';
import { useModalContext } from '@/components/ui/modal-provider';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useAiGenerationLock } from '@/hooks/use-ai-generation-lock';
import { getAiUsageMessage, isAiUsageExhausted } from '@/lib/ai-usage';
import { useReportWizard } from '@/pages/create-report/context/ReportWizardContext';
import { BehaviorObservationSurvey } from '@/types/report';
import { buildAiBusyReason, getGeneratingSectionTitle } from './ai-generation-lock';
import BehaviorObservationSurveyModal from './BehaviorObservationSurveyModal';
import BehaviorObservationTemplatesModal from './BehaviorObservationTemplatesModal';
import { SECTIONS } from './constants';
import SidebarActionCard from './SidebarActionCard';
import { WizardStepProps } from './types';

/**
 * Wraps a card that may be unavailable so it can say why.
 *
 * The trigger is always mounted and only the content is conditional — mounting
 * the whole tooltip on demand would remount the card underneath it. The `div`
 * is not decorative: `SidebarActionCard` renders a natively disabled `<button>`,
 * which swallows the hover the trigger needs.
 */
function BlockedCardTooltip({ reason, children }: { reason: string | null; children: ReactNode }) {
    return (
        <TooltipProvider delayDuration={0}>
            <Tooltip>
                <TooltipTrigger asChild>
                    <div className="w-full">{children}</div>
                </TooltipTrigger>
                {reason ? (
                    <TooltipContent
                        side="top"
                        align="start"
                        className="max-w-xs whitespace-normal px-3 py-2 text-sm leading-relaxed"
                    >
                        {reason}
                    </TooltipContent>
                ) : null}
            </Tooltip>
        </TooltipProvider>
    );
}

export default function ObservationsStep({ data, onUpdate }: WizardStepProps) {
    const { openWizardModal, closeModal } = useModalContext();
    const {
        silentSave,
        isGenerating,
        setIsGenerating,
        setGeneratingSectionId,
        generatingSectionId,
        sidebarSteps,
        beginGeneration,
        endGeneration,
    } = useReportWizard();
    const { isGeneratingThis } = useAiGenerationLock(SECTIONS.BEHAVIOR_OBSERVATIONS);
    const hasReportId = Boolean(data.id);
    const { data: aiUsage } = useQuery({
        queryKey: ['ai-usage'],
        queryFn: aiApi.getUsage,
        staleTime: 30_000,
        enabled: hasReportId,
    });
    const canUseOptions = hasReportId && !isGenerating;
    // Deliberately not gated on `canUseOptions`: tying it to that made the
    // out-of-credits reason vanish the moment any generation started, dropping
    // the card to a silently disabled one exactly when a second reason arrived.
    const isSurveyBlockedByUsage = hasReportId && isAiUsageExhausted(aiUsage);
    const aiUsageMessage = getAiUsageMessage(aiUsage);

    const otherGenerationReason =
        isGenerating && !isGeneratingThis
            ? buildAiBusyReason(getGeneratingSectionTitle(generatingSectionId, sidebarSteps))
            : null;
    // While this section generates, the card already says "Gerando texto..." —
    // credits outrank the lock because they outlive it.
    const surveyBlockedReason = isGeneratingThis
        ? null
        : (isSurveyBlockedByUsage && aiUsageMessage) || otherGenerationReason || null;
    const templatesBlockedReason = isGeneratingThis
        ? 'O texto desta seção está sendo gerado. Interrompa para usar um template.'
        : otherGenerationReason;

    const handleSubmitSurvey = async (survey: BehaviorObservationSurvey) => {
        if (!data.id) return;

        setIsGenerating(true);
        setGeneratingSectionId(SECTIONS.BEHAVIOR_OBSERVATIONS);
        const generation = beginGeneration();
        await silentSave();
        try {
            onUpdate({
                behavior_observations_survey: survey,
                behavior_observations: {
                    ...data.behavior_observations,
                    section_title:
                        data.behavior_observations?.section_title || 'Observação do Comportamento',
                    content: '',
                },
            });

            const response = await aiApi.generate(
                data.id,
                SECTIONS.BEHAVIOR_OBSERVATIONS,
                {
                    behavior_observation_survey: survey,
                },
                'create',
                undefined,
                generation.options,
            );

            onUpdate({
                behavior_observations: {
                    ...data.behavior_observations,
                    section_title:
                        data.behavior_observations?.section_title || 'Observação do Comportamento',
                    content: response.content,
                },
            });

            toast.success('Texto gerado com sucesso');
            closeModal();
        } catch (error: unknown) {
            // The user stopped it. The section is untouched and no credit was
            // spent, and the button they pressed is answer enough.
            if (isAiGenerationCancelled(error)) return;

            const message =
                error instanceof Error && error.message ? error.message : 'Erro ao gerar o texto.';

            toast.error(message);
            throw error;
        } finally {
            setIsGenerating(false);
            setGeneratingSectionId(null);
            endGeneration();
        }
    };

    const handleOpenSurvey = () => {
        if (!hasReportId) return;

        openWizardModal({
            title: null,
            size: 'xl',
            className: 'max-h-[90vh]',
            content: (
                <BehaviorObservationSurveyModal
                    localSurvey={data.behavior_observations_survey}
                    reportId={data.id}
                    patientAge={data.patient?.age}
                    patientBirthDate={data.patient?.date_of_birth}
                    examDate={data.tests_performed_at_start}
                    onServerSurveyLoaded={(survey) =>
                        onUpdate({ behavior_observations_survey: survey })
                    }
                    onCancel={closeModal}
                    onSubmit={handleSubmitSurvey}
                />
            ),
        });
    };

    const handleOpenTemplates = () => {
        if (!hasReportId) return;

        openWizardModal({
            title: null,
            size: 'xl',
            content: (
                <BehaviorObservationTemplatesModal
                    existingContent={data.behavior_observations?.content}
                    onCancel={closeModal}
                    onSelect={(text) => {
                        onUpdate({
                            behavior_observations: {
                                ...data.behavior_observations,
                                section_title:
                                    data.behavior_observations?.section_title ||
                                    'Observação do Comportamento',
                                content: text,
                            },
                        });
                        closeModal();
                        toast.success('Texto inserido com sucesso');
                    }}
                />
            ),
        });
    };

    return (
        <div className="flex h-full flex-col gap-4">
            <div className="space-y-3">
                <BlockedCardTooltip reason={surveyBlockedReason}>
                    <SidebarActionCard
                        onClick={handleOpenSurvey}
                        disabled={!canUseOptions || isSurveyBlockedByUsage}
                        title="Questionário rápido"
                        description="Estruture a observação com checkboxes e gere um texto clínico coeso em poucos minutos."
                        icon={<ClipboardText className="size-5 text-muted-foreground" />}
                    >
                        {isGeneratingThis && (
                            <p className="mt-2 text-xs text-blue-600">Gerando texto...</p>
                        )}
                    </SidebarActionCard>
                </BlockedCardTooltip>

                <BlockedCardTooltip reason={templatesBlockedReason}>
                    <SidebarActionCard
                        onClick={handleOpenTemplates}
                        disabled={!canUseOptions}
                        title="Modelos prontos"
                        description="Escolha entre modelos prontos para acelerar a escrita clínica."
                        icon={<DocumentText className="size-5 text-muted-foreground" />}
                    />
                </BlockedCardTooltip>
            </div>
        </div>
    );
}
